import axiosInstance from "@/lib/axiosInstance";
import axios from "axios";
import type {
  ActivityDayLog,
  ActivityMemberStatus,
  ActivityResponseDO,
  FetchActivityParams,
} from "@/types/activity";
import { getApiErrorMessage } from "@/lib/apiError";

const normalizeActivityStatus = (
  status: string | null | undefined,
): ActivityMemberStatus => {
  switch (status?.trim().toLowerCase()) {
    case "active":
      return "Active";
    case "expiring soon":
    case "expiring_soon":
      return "Expiring Soon";
    case "expired":
      return "Expired";
    case "dormant":
      return "Dormant";
    default:
      return null;
  }
};

const normalizeActivityResponse = (
  response: ActivityResponseDO,
): ActivityResponseDO => ({
  stats: response.stats,
  dayLogs: response.dayLogs.map(
    (entry): ActivityDayLog => ({
      id: entry.id,
      memberId: entry.memberId,
      member: entry.member ?? "",
      time: entry.time,
      checkedInAt: entry.checkedInAt,
      checkedOutAt: entry.checkedOutAt ?? null,
      checkInRecorder: entry.checkInRecorder,
      checkOutRecorder: entry.checkOutRecorder,
      sessionBucketKey: entry.sessionBucketKey,
      sessionBucketName: entry.sessionBucketName || "Gym access",
      canCheckOut: entry.canCheckOut,
      addOnAttention: entry.addOnAttention,
      status: normalizeActivityStatus(entry.status),
      ...(entry.isCrossBranchVisit === true
        ? { isCrossBranchVisit: true }
        : {}),
      ...("homeBranchId" in entry
        ? { homeBranchId: entry.homeBranchId ?? null }
        : {}),
      ...("homeBranchName" in entry
        ? { homeBranchName: entry.homeBranchName ?? null }
        : {}),
      avatar: entry.avatar ?? null,
    }),
  ),
  pagination: response.pagination,
});

const getExportFilename = (contentDisposition?: string) => {
  const encodedFilename = contentDisposition?.match(
    /filename\*=UTF-8''([^;]+)/i,
  )?.[1];
  const regularFilename = contentDisposition?.match(
    /filename\s*=\s*"?([^";]+)"?/i,
  )?.[1];
  const filename = encodedFilename
    ? decodeURIComponent(encodedFilename)
    : regularFilename?.trim();

  return (filename || "activity.csv").replace(/[\\/]/g, "_");
};

const buildActivityParams = (params: FetchActivityParams) => ({
  limit: params.limit,
  cursor: params.cursor || undefined,
  search: params.search?.trim() || undefined,
  status: params.status || undefined,
  startDate: params.startDate || undefined,
  endDate: params.endDate || undefined,
});

const getActivityExportErrorMessage = async (
  error: unknown,
  fallback: string,
) => {
  if (!axios.isAxiosError(error)) {
    return fallback;
  }

  const responseData = error.response?.data;

  if (responseData instanceof Blob) {
    try {
      const text = await responseData.text();
      const parsed = JSON.parse(text) as { message?: string; error?: string };
      return parsed.message || parsed.error || fallback;
    } catch {
      return fallback;
    }
  }

  return getApiErrorMessage(error, fallback);
};

// Activity service
export const activityService = {
  async fetchActivity(params: FetchActivityParams = {}) {
    try {
      const response = await axiosInstance.get<ActivityResponseDO>("/activity", {
        params: buildActivityParams(params),
      });
      return normalizeActivityResponse(response.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Fetch Failed");
      }
      throw new Error("An unexpected error occurred during fetch for activity");
    }
  },

  async exportActivity(
    params: Pick<FetchActivityParams, "startDate" | "endDate">,
  ) {
    try {
      const response = await axiosInstance.get<Blob>("/activity/export", {
        params: {
          startDate: params.startDate || undefined,
          endDate: params.endDate || undefined,
        },
        responseType: "blob",
      });

      return {
        blob: response.data,
        filename: getExportFilename(response.headers["content-disposition"]),
      };
    } catch (error) {
      throw new Error(
        await getActivityExportErrorMessage(error, "Activity export failed"),
      );
    }
  },
};
